import { createSelector } from "reselect"
import { get, chain, take, reduce, slice, sumBy } from "lodash"
import pluralize from "pluralize"
import { getFactorsMetadata } from "selectors/factors"
import { getMustAggregateFactors } from "modules/portfolio-root/selectors/portfolios"
import { FACTORS_THRESHOLD } from "helpers/analyzeConstants"
import { getIsAggregatedFactor } from "helpers/factors"
import { aggregateExposuresFactors, aggregateFactorsSumBy, AGGREGATION_FUNCTIONS } from "helpers/factorClassification"
import { getVolatility } from "./volatility"
import { getModelFactorsClassification } from "selectors/modelMetadata"

const getCategories = (state, props) => props.categories
const getDailyRisk = (state, props) => props.dailyRisk

const getExposureFactors = state => get(state, "global.data.riskDetails.data.model.simulation.exposure[0].factors")

const getExposure = createSelector(
  [getExposureFactors, getMustAggregateFactors, getModelFactorsClassification],
  (factors, mustAggregateFactors, factorsClassification) => {
    let factorsExposures = factors
    if (mustAggregateFactors) {
      factorsExposures = aggregateExposuresFactors(factorsClassification, factorsExposures, AGGREGATION_FUNCTIONS.MAX, "net")
    }
    return chain(factorsExposures)
      .keyBy("id")
      .mapValues(item => item.net)
      .value()
  },
)

function getTakePlus(count, combine, accumulator) {
  return (values) => {
    if (values.length < count) {
      return values
    } else {
      const reduced = reduce(slice(values, 5), combine, accumulator)
      return [...take(values, 5), reduced]
    }
  }
}

export const getRiskByCategory = createSelector(
  [getDailyRisk, getMustAggregateFactors, getModelFactorsClassification],
  (risk, mustAggregateFactors, factorsClassification) => chain(risk)
    .get("attribution.factors")
    .groupBy(item => item.category)
    .mapValues(items => {
      let factors = items
      if (mustAggregateFactors) {
        factors = aggregateFactorsSumBy(factorsClassification, factors, "value")
      }
      return {
        total: sumBy(factors, item => item.value),
        factors,
      }
    })
    .value(),
)

export const getDetails = createSelector(
  [getCategories, getRiskByCategory, getFactorsMetadata, getExposure, getVolatility],
  (categories, riskByCategory, factorsMetadata, exposures, volatility) =>
    categories
      .filter(category => category.listFactors)
      .map(category => ({
        total: get(riskByCategory, [category.id, "total"], 0),
        factors: chain(riskByCategory)
          .get([category.id, "factors"])
          .map(({ value, id, name, category }) => ({
            id,
            value,
            label: name,
            category,
            isAggregated: getIsAggregatedFactor(factorsMetadata, id),
            volatility: get(volatility, id),
            exposure: get(exposures, id),
          }))
          .orderBy(item => Math.abs(item.value), "desc")
          .thru(getTakePlus(5, (accumulator, factor) => {
            accumulator.value += factor.value
            return accumulator
          }, { id: "other", label: `Other ${pluralize(category.label)}`, value: 0 }))
          .filter(factor => Math.abs(factor.value) > FACTORS_THRESHOLD)
          .value(),
        ...category })),
)

export const getTopTotal = createSelector(
  [getCategories, getRiskByCategory],
  (categories, riskByCategory) => chain(categories)
    .filter(category => category.listFactors)
    .map(category => get(riskByCategory, [category.id, "total"], 0))
    .max()
    .value(),
)
